import { defineStore } from 'pinia';
import { defaultLocale, i18n } from '../../../lang';
import { cloneDeep, filter, join, map, orderBy, size, split, uniqBy } from 'lodash';
import { LocalStorageService } from '@/shared/services/local-storage.service';

const localStorage = new LocalStorageService();

export interface INotificationMessage {
    title: string;
    message: string;
    type: 'error' | 'success' | 'info' | 'warning';
    action: unknown;
}

export interface IMessage {
    completed_at: string;
    message: string[];
    started_at: string;
    status: string;
    task_id: string;
}

export interface IUIState {
    loading: boolean;
    notification: INotificationMessage[];
    searchString: string;
    searchType: 'machines' | 'implements';
    searchOnFocus: boolean;
    locale: string;
    tasksId: { task_id: string; file_name: string }[];
    messages: IMessage[];
    nextUrl: string | null;
}

const initialStateUIStore: IUIState = {
    loading: false,
    notification: [],
    searchString: '',
    searchType: 'machines',
    searchOnFocus: false,
    locale: defaultLocale,
    tasksId: [],
    messages: [],
    nextUrl: null,
};

const useUIStore = defineStore('ui', {
    state: () => ({ ...initialStateUIStore }),
    getters: {
        selectLading: (state): boolean => state.loading,
        selectSearchOnFocus: (state): boolean => state.searchOnFocus,
        selectSearchString: (state): string => state.searchString,
        selectSearchType: (state): 'machines' | 'implements' => state.searchType,
        selectNotification: (state): INotificationMessage[] => state.notification,
        selectTasks: (state): { task_id: string; file_name: string }[] => state.tasksId,
        selectMessage: (state): IMessage[] => {
            const messages = cloneDeep(state.messages);
            const orderMessages = orderBy(messages, ['completed_at'], ['desc']);
            return orderMessages;
        },
        selectNextUrl(state): string | null {
            return state.nextUrl;
        },
    },
    actions: {
        setNextUrl(nextUrl: string | null) {
            this.nextUrl = nextUrl;
        },
        setMessage(message: IMessage) {
            const messages = cloneDeep(this.messages);
            messages.push(message);
            this.messages = uniqBy(messages, 'task_id');
        },
        setMessages(messages: IMessage[]) {
            this.messages = messages;
        },
        completeMessages(task_id: string) {
            const uncompletedMsg: IMessage[] = filter(cloneDeep(this.messages), (x: IMessage) => x.task_id !== task_id);
            localStorage.setItem('tasks_id', join(uncompletedMsg));
            this.messages = uncompletedMsg;
        },
        setTasksId(tasks: string[]) {
            if (!tasks || !size(tasks)) {
                return;
            }
            const mappedTasks: { task_id: string; file_name: string }[] = map(tasks, (task) => {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                const [file_name, task_id] = split(JSON.parse(task) ?? '|', '|');
                return { task_id, file_name };
            });
            this.tasksId = mappedTasks;
        },
        setLoading(loading: boolean) {
            this.loading = loading;
        },
        setSearchOnFocus(onFocus: boolean) {
            this.searchOnFocus = onFocus;
        },
        setSearchString(searchString: string) {
            this.searchString = searchString;
        },
        setSearchType(searchType: 'machines' | 'implements') {
            this.searchType = searchType;
        },
        setNotification(notification: INotificationMessage) {
            this.notification = [notification];
        },
        resetNotification() {
            this.notification = initialStateUIStore.notification;
        },
        setLocale(locale: string) {
            this.locale = locale;
            i18n.global.locale.value = locale;
        },
    },
});

export default useUIStore;
